import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  NgbDateStruct,
  NgbDatepickerModule,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { dateToNgbDate, ngbDateToDate } from '@doc/utils';

@Component({
  selector: 'doc-ngb-datepicker',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDatepickerModule],
  template: `
    <div
      class="doc-ngb-datepicker"
      [class.full-width]="fullWidth"
      [ngClass]="containerClass">
      <input
        #dateInput
        class="form-control"
        [ngClass]="inputClass"
        [id]="inputId || null"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [readonly]="readonly"
        ngbDatepicker
        #datepicker="ngbDatepicker"
        [container]="$any(container)"
        [ngModel]="internalValue"
        (ngModelChange)="onInternalChange($event)"
        (dateSelect)="onDateSelected($event)"
        [minDate]="$any(minNgbDate)"
        [maxDate]="$any(maxNgbDate)" />
      @if (showIcon) {
        <button
          type="button"
          class="input-icon-btn"
          (click)="toggle()"
          [disabled]="disabled">
          <ng-content select="[dateIcon]"></ng-content>
          @if (!hasProjectedIcon) {
            <i class="mdi mdi-calendar"></i>
          }
        </button>
      }
      @if (showClear && internalValue) {
        <button
          type="button"
          class="clear-icon-btn"
          (click)="clear(); $event.stopPropagation()"
          [disabled]="disabled"
          aria-label="Clear date">
          <i class="pi pi-times"></i>
        </button>
      }
    </div>
  `,
  styleUrl: './doc-ngb-datepicker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocNgbDatepickerComponent),
      multi: true,
    }],
})
export class DocNgbDatepickerComponent implements ControlValueAccessor, AfterContentInit, AfterViewInit {
  @Input() placeholder = '';
  @Input() inputId = '';
  @Input() inputClass = '';
  @Input() containerClass = '';
  @Input() fullWidth = true;
  @Input() showIcon = true;
  @Input() showClear = false;
  @Input() readonly = false;
  @Input() container?: 'body' | string | null;

  @ContentChild('[dateIcon]', { read: ElementRef }) projectedIconAttr?: ElementRef;
  @ContentChild('dateIcon', { read: ElementRef }) projectedIconRef?: ElementRef;

  @Input('disabled')
  set disabledInput(value: boolean | string | null | undefined) {
    this.disabled = value === true || value === '';
  }

  @Input()
  set minDate(value: Date | null | undefined) {
    this.minNgbDate = dateToNgbDate(value ?? null);
  }

  @Input()
  set maxDate(value: Date | null | undefined) {
    this.maxNgbDate = dateToNgbDate(value ?? null);
  }

  @Output() dateSelect = new EventEmitter<Date | null>();
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('datepicker') private datepicker?: NgbInputDatepicker;
  @ViewChild('dateInput') private dateInput?: ElementRef<HTMLInputElement>;

  private readonly elRef = inject(ElementRef);

  internalValue: NgbDateStruct | null = null;
  minNgbDate: NgbDateStruct | null = null;
  maxNgbDate: NgbDateStruct | null = null;
  hasProjectedIcon = false;
  disabled = false;

  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngAfterContentInit(): void {
    this.checkProjectedIcon();
  }

  ngAfterViewInit(): void {
    this.checkProjectedIcon();
  }

  private checkProjectedIcon(): void {
    const projected = !!(
      this.projectedIconAttr ||
      this.projectedIconRef ||
      this.elRef?.nativeElement?.querySelector('[dateIcon]') ||
      this.elRef?.nativeElement?.querySelector('app-dynamic-icon')
    );
    this.hasProjectedIcon = projected;
  }

  writeValue(value: Date | null): void {
    this.internalValue = dateToNgbDate(value);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInternalChange(value: NgbDateStruct | null): void {
    this.internalValue = value;
    const date = ngbDateToDate(value);
    this.onChange(date);
    this.onTouched();
    this.dateSelect.emit(date);
  }

  onDateSelected(value: NgbDateStruct): void {
    this.dateSelect.emit(ngbDateToDate(value));
  }

  toggle(): void {
    this.datepicker?.toggle();
  }

  open(): void {
    this.datepicker?.open();
  }

  close(): void {
    this.datepicker?.close();
  }

  focusInput(): void {
    this.dateInput?.nativeElement?.focus();
    this.datepicker?.open();
  }

  clear(): void {
    this.internalValue = null;
    this.onChange(null);
    this.onTouched();
    this.dateSelect.emit(null);
    this.cleared.emit();
  }
}
